/**
 * Mutation defaults for every queueable write.
 *
 * This is the piece that makes offline capture actually work. A mutation
 * restored from disk carries only its key and its variables — the function it
 * was built with cannot be serialised. Without a matching `setMutationDefaults`
 * the rehydrated mutation has no `mutationFn`, so `resumePausedMutations`
 * quietly does nothing and the user's write is gone. Registering defaults for
 * every key in OFFLINE_MUTATION_KEYS is what turns the persisted record back
 * into a runnable request.
 *
 * The kinds match mobile's one-for-one; only the endpoints behind them differ,
 * because the two clients complete a task through different routes.
 */
import type { CreateTimeEntryRequest } from '@/lib/api/time-entries.api'
import type { OwnedThreadCommentPayload } from '@/modules/threads/comment-commands.api'
import type { ThreadDraftOwner } from '@/modules/threads/drafts'
import type { QueryClient } from '@tanstack/react-query'
import { getCurrentClientPortalContext } from '@/lib/api/client-portal-context'
import { resolveOrganizationContext } from '@/lib/api/organization-context'
import { tasksApi } from '@/lib/api/tasks.api'
import { timeEntryApi } from '@/lib/api/time-entries.api'
import { getWorkspaceSessionSnapshot } from '@/lib/session/workspace-session'
import { createThreadCommentCommandFn } from '@/modules/threads/comment-commands.api'
import { createThreadCreationCommandFn } from '@/modules/threads/creation-commands.api'
import { readRegisteredPortalThreadOwner } from '@/modules/threads/portal-thread-owner'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'

import type {
  CreateTask,
  TaskWithAssignments,
  TimeEntry,
  UpdateTaskInput,
} from '@scrambl/shared'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import type {
  OfflineMutationEnvelope,
  OfflineMutationKind,
} from './offline-queue'
import { offlineRunnerContext } from './ledger'
import {
  classifyReplayError,
  createScopedOfflineMutationFn,
  isTerminalOfflineWriteError,
  OFFLINE_MUTATION_KEYS,
  OFFLINE_QUEUE_MAX_ATTEMPTS,
} from './offline-queue'

export interface TaskCreatePayload {
  data: CreateTask
}

/**
 * Web completes a task by patching its status field rather than through a
 * dedicated status route, so the payload is a resolved update body. Replay
 * carries the original row revision; an ambiguous delivery stays for review.
 */
export interface TaskStatusPayload {
  taskId: string
  data: UpdateTaskInput
}

export interface TimeEntryCreatePayload {
  data: CreateTimeEntryRequest
}

export type ThreadCommentPayload = OwnedThreadCommentPayload

/** Routing selection; the application service still decides current record rights. */
export function selectedCommentOwner(
  queryClient?: QueryClient,
): ThreadDraftOwner | null {
  const portalEntityId = getCurrentClientPortalContext()?.entityId ?? null
  if (portalEntityId) {
    if (!queryClient) return null
    const scope = getWorkspaceSessionSnapshot()
    if (scope.ownerUserId !== offlineRunnerContext.getOwnerId()) return null
    return readRegisteredPortalThreadOwner(queryClient, scope, portalEntityId)
  }
  const selection = useOrganizationStore.getState()
  const userId = offlineRunnerContext.getOwnerId()
  const membership = selection.currentOrganization?.userMembership
  if (!userId || (membership && membership.status !== 'ACTIVE')) return null
  try {
    const context = resolveOrganizationContext(selection, {}, true)
    if (!context.orgId) return null
    return {
      userId,
      organizationId: context.orgId,
      portalEntityId: getCurrentClientPortalContext()?.entityId ?? null,
      externalRelationshipId: context.externalRelationshipId,
    }
  } catch {
    return null
  }
}

function commentAccessStamp() {
  const selected = useOrganizationStore.getState()
  const membership = selected.currentOrganization?.userMembership
  const relationship = selected.currentOrganization?.isExternalAccess
    ? selected.externalOrgRelationship
    : null
  return JSON.stringify([
    useAuth.getState().user?.role,
    selected.currentOrganization?.id,
    membership?.id,
    membership?.role,
    membership?.status,
    relationship?.id,
    relationship?.hostOrganizationId,
    relationship?.status,
    relationship?.revokedAt,
    relationship?.permissionSet,
  ])
}
const commentRunnerContext = {
  ...offlineRunnerContext,
  captureSession() {
    const scope = offlineRunnerContext.captureSession?.()
    const stamp = commentAccessStamp()
    return {
      confirmCurrent: scope?.confirmCurrent?.bind(scope),
      assertCurrent() {
        scope?.assertCurrent()
        if (stamp !== commentAccessStamp()) throw new SessionChangedError()
      },
    }
  },
}

const { getStatus } = offlineRunnerContext

/**
 * Refuse to replay a write into a different organisation than it was made in.
 *
 * On web the org is a request *header* applied globally by the API client, not
 * a parameter on the call — so a write queued in one org and replayed after the
 * user switches would silently land in the other one. Both clients require
 * the original workspace to be selected before replaying its captured intent.
 */
function createWorkspaceMutationFn<TPayload, TResult>(
  kind: OfflineMutationKind,
  run: (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult>,
): (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult> {
  return createScopedOfflineMutationFn(kind, run, offlineRunnerContext, {
    organizationId: () =>
      useOrganizationStore.getState().currentOrganization?.id ?? null,
  })
}

/**
 * `networkMode: 'online'` matters more than it looks. It parks the mutation
 * *before* `mutationFn` touches the network, so a paused write is provably
 * un-sent and replaying it carries no duplicate risk. The alternative
 * (`offlineFirst`) would run the request anyway, fail on the dead socket, and
 * leave us unable to tell "never sent" from "sent, reply lost".
 */
const sharedDefaults = {
  networkMode: 'online' as const,
  retry: (failureCount: number, error: unknown) =>
    !isTerminalOfflineWriteError(error) &&
    failureCount < OFFLINE_QUEUE_MAX_ATTEMPTS - 1 &&
    classifyReplayError(getStatus(error)) === 'retry',
  retryDelay: (attemptIndex: number) =>
    Math.min(1000 * 2 ** attemptIndex, 30000),
  // Replayed writes are silent. The pending-writes popover is where their
  // outcome is reported; a toast for something the user did in a tunnel two
  // hours ago is noise they cannot act on.
  meta: { silent: true },
}

export function registerOfflineMutationDefaults(
  queryClient: QueryClient,
): void {
  queryClient.setMutationDefaults([...OFFLINE_MUTATION_KEYS['thread.create']], {
    ...sharedDefaults,
    mutationFn: createThreadCreationCommandFn(commentRunnerContext, () =>
      selectedCommentOwner(queryClient),
    ) as (variables: unknown) => Promise<unknown>,
  })
  queryClient.setMutationDefaults([...OFFLINE_MUTATION_KEYS['task.create']], {
    ...sharedDefaults,
    mutationFn: createWorkspaceMutationFn<
      TaskCreatePayload,
      TaskWithAssignments
    >('task.create', envelope =>
      tasksApi.createTask(envelope.payload.data),
    ) as (variables: unknown) => Promise<TaskWithAssignments>,
  })

  queryClient.setMutationDefaults([...OFFLINE_MUTATION_KEYS['task.status']], {
    ...sharedDefaults,
    mutationFn: createWorkspaceMutationFn<
      TaskStatusPayload,
      TaskWithAssignments
    >('task.status', envelope =>
      tasksApi.updateTask(envelope.payload.taskId, {
        ...envelope.payload.data,
        expectedRevision: envelope.recordRevision,
      }),
    ) as (variables: unknown) => Promise<TaskWithAssignments>,
  })

  queryClient.setMutationDefaults(
    [...OFFLINE_MUTATION_KEYS['time-entry.create']],
    {
      ...sharedDefaults,
      mutationFn: createWorkspaceMutationFn<TimeEntryCreatePayload, TimeEntry>(
        'time-entry.create',
        envelope => timeEntryApi.createTimeEntry(envelope.payload.data),
      ) as (variables: unknown) => Promise<TimeEntry>,
    },
  )

  queryClient.setMutationDefaults(
    [...OFFLINE_MUTATION_KEYS['thread-comment.create']],
    {
      ...sharedDefaults,
      mutationFn: createThreadCommentCommandFn(commentRunnerContext, () =>
        selectedCommentOwner(queryClient),
      ) as (variables: unknown) => Promise<unknown>,
    },
  )
}
